import React, { useState, useEffect } from 'react';
import {
  Package, Plus, Search, ChevronLeft, Loader2, Edit2,
  ArrowDown, ArrowUp, AlertTriangle, Warehouse, Tag,
  Hash, Eye, BarChart3, RefreshCw, X, Trash2,
  TrendingDown, TrendingUp, Box, ShoppingCart, Download
} from 'lucide-react';

import api from '../../services/api';
import { Toast } from '../ui/Toast';
import { useLoader } from '../../contexts/LoaderContext';
import { useParamsCompta } from '../../contexts/ParametresComptaContext';

const TYPE_MVT = {
  entree: { label: 'Entrée', color: '#10b981', bg: '#d4f4dd', icon: ArrowDown },
  sortie: { label: 'Sortie', color: '#ef4444', bg: '#fee2e2', icon: ArrowUp },
  ajustement: { label: 'Ajustement', color: '#f59e0b', bg: '#fef3c7', icon: RefreshCw },
};

const MOTIFS = [
  { value: 'achat', label: 'Achat' },
  { value: 'vente', label: 'Vente' },
  { value: 'retour', label: 'Retour' },
  { value: 'perte', label: 'Perte / Casse' },
  { value: 'inventaire', label: 'Inventaire' },
  { value: 'transfert', label: 'Transfert' },
  { value: 'production', label: 'Production' },
  { value: 'autre', label: 'Autre' },
];

const StocksModule = () => {
  const [articles, setArticles] = useState([]);
  const [totaux, setTotaux] = useState({});
  const [categories, setCategories] = useState([]);
  const [entrepots, setEntrepots] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategorie, setFilterCategorie] = useState('');
  const [filterAlerte, setFilterAlerte] = useState(false);

  // Vues
  const [currentView, setCurrentView] = useState('list');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Modal article
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [articleMode, setArticleMode] = useState('create');
  const [editingArticle, setEditingArticle] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [articleForm, setArticleForm] = useState({
    reference: '', designation: '', description: '', categorie: '', unite: 'unité',
    prix_achat: 0, prix_vente: 0, stock_actuel: 0, stock_minimum: 0,
    entrepot_id: '', code_barre: '', notes: ''
  });

  // Modal mouvement
  const [showMvtModal, setShowMvtModal] = useState(false);
  const [mvtForm, setMvtForm] = useState({
    article_id: '', type_mouvement: 'entree', quantite: '', motif: 'autre',
    description: '', date_mouvement: new Date().toISOString().split('T')[0], prix_unitaire: ''
  });

  // Modal entrepôt
  const [showEntrepotModal, setShowEntrepotModal] = useState(false);
  const [entrepotForm, setEntrepotForm] = useState({ nom: '', adresse: '', couleur: '#448696' });

  const { showLoader, hideLoader } = useLoader();
  const { formatMontant, symbole } = useParamsCompta();
  const showToast = (m, t = 'success') => setToast({ show: true, message: m, type: t });

  useEffect(() => {
    if (toast.show) { const t = setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000); return () => clearTimeout(t); }
  }, [toast.show]);

  useEffect(() => { loadArticles(); loadEntrepots(); }, []);
  useEffect(() => { loadArticles(); }, [filterCategorie, filterAlerte]);
  useEffect(() => { const t = setTimeout(() => loadArticles(), 300); return () => clearTimeout(t); }, [searchTerm]);

  const loadArticles = async () => {
    showLoader(); setIsLoading(true);
    try {
      const params = { search: searchTerm, categorie: filterCategorie };
      if (filterAlerte) params.alerte = '1';
      const r = await api.stocks.getArticles(params);
      if (r.success) { setArticles(r.data || []); setTotaux(r.totaux || {}); setCategories(r.categories || []); }
    } catch (e) { console.error(e); }
    finally { setIsLoading(false); hideLoader(); }
  };

  const loadEntrepots = async () => {
    try { const r = await api.stocks.getEntrepots(); if (r.success) setEntrepots(r.data || []); } catch (e) {}
  };

  const loadDetail = async (article) => {
    setCurrentView('detail'); setLoadingDetail(true);
    try {
      const r = await api.stocks.getArticle(article.id);
      if (r.success) setSelectedArticle(r.data);
    } catch (e) { console.error(e); }
    finally { setLoadingDetail(false); }
  };

  // ============================================================================
  // ARTICLES CRUD
  // ============================================================================
  const openCreateArticle = () => {
    setArticleMode('create'); setEditingArticle(null);
    setArticleForm({ reference: '', designation: '', description: '', categorie: '', unite: 'unité', prix_achat: 0, prix_vente: 0, stock_actuel: 0, stock_minimum: 0, entrepot_id: '', code_barre: '', notes: '' });
    setShowArticleModal(true);
  };
  const openEditArticle = (a) => {
    setArticleMode('edit'); setEditingArticle(a);
    setArticleForm({ reference: a.reference, designation: a.designation, description: a.description || '', categorie: a.categorie || '', unite: a.unite || 'unité', prix_achat: a.prix_achat, prix_vente: a.prix_vente, stock_actuel: a.stock_actuel, stock_minimum: a.stock_minimum, entrepot_id: a.entrepot_id || '', code_barre: a.code_barre || '', notes: a.notes || '' });
    setShowArticleModal(true);
  };
  const handleSaveArticle = async () => {
    if (!articleForm.reference.trim() || !articleForm.designation.trim()) { showToast('Référence et désignation obligatoires', 'error'); return; }
    setIsSaving(true);
    try {
      const r = articleMode === 'create' ? await api.stocks.creerArticle(articleForm) : await api.stocks.modifierArticle(editingArticle.id, articleForm);
      if (r.success) { showToast(r.message); setShowArticleModal(false); await loadArticles(); if (selectedArticle && articleMode === 'edit') loadDetail({ id: editingArticle.id }); }
      else showToast(r.message || 'Erreur', 'error');
      
    } catch (e) { const msg = e.response?.data?.message || e.message || 'Erreur'; showToast(msg, 'error'); }
    finally { setIsSaving(false); }
  };

  // ============================================================================
  // MOUVEMENTS
  // ============================================================================
  const openMvtModal = (articleId = '', type = 'entree') => {
    const art = articles.find(a => a.id === parseInt(articleId));
    setMvtForm({ article_id: articleId, type_mouvement: type, quantite: '', motif: type === 'entree' ? 'achat' : type === 'sortie' ? 'vente' : 'inventaire', description: '', date_mouvement: new Date().toISOString().split('T')[0], prix_unitaire: art ? art.prix_achat : '' });
    setShowMvtModal(true);
  };
  const handleSaveMvt = async () => {
    if (!mvtForm.article_id || !mvtForm.quantite) { showToast('Article et quantité obligatoires', 'error'); return; }
    setIsSaving(true);
    try {
      const r = await api.stocks.creerMouvement(mvtForm);
      if (r.success) { showToast(r.message); setShowMvtModal(false); await loadArticles(); if (selectedArticle) loadDetail({ id: mvtForm.article_id }); }
     else showToast(r.message || 'Erreur', 'error');
    } catch (e) { const msg = e.response?.data?.message || e.message || 'Erreur'; showToast(msg, 'error'); }
    finally { setIsSaving(false); }
  };

  // ============================================================================
  // ENTREPÔTS

  // ============================================================================
  // ENTREPÔTS
  // ============================================================================
  const handleSaveEntrepot = async () => {
    if (!entrepotForm.nom.trim()) { showToast('Nom obligatoire', 'error'); return; }
    setIsSaving(true);
    try {
      const r = await api.stocks.creerEntrepot(entrepotForm);
      if (r.success) { showToast(r.message); setShowEntrepotModal(false); await loadEntrepots(); setEntrepotForm({ nom: '', adresse: '', couleur: '#448696' }); }
      else showToast(r.message || 'Erreur', 'error');
    } catch (e) { const msg = e.response?.data?.message || e.message || 'Erreur'; showToast(msg, 'error'); }
    finally { setIsSaving(false); }
  };


  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '-';
  const inputStyle = {
    onFocus: (e) => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(92,149,163,0.1)'; },
    onBlur: (e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; },
  };

  // ============================================================================
  // VUE LISTE
  // ============================================================================
  const renderList = () => (
    <>
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #448696', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-2"><Package size={18} style={{ color: '#448696' }} /><span className="text-xs font-semibold text-gray-500">Articles</span></div>
          <div className="text-2xl font-bold" style={{ color: '#1a1a2e' }}>{totaux.total || 0}</div>
          <div className="text-xs text-gray-400 mt-0.5">{totaux.nb_categories || 0} catégories</div>
        </div>
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #6366f1', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-2"><BarChart3 size={18} className="text-indigo-600" /><span className="text-xs font-semibold text-gray-500">Valeur stock</span></div>
          <div className="text-lg font-bold text-indigo-600">{formatMontant(totaux.valeur_stock)}</div>
          <div className="text-xs text-gray-400 mt-0.5">Prix d'achat</div>
        </div>
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #f59e0b', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-2"><AlertTriangle size={18} className="text-amber-600" /><span className="text-xs font-semibold text-gray-500">Alertes</span></div>
          <div className="text-2xl font-bold text-amber-600">{totaux.nb_alertes || 0}</div>
          <div className="text-xs text-gray-400 mt-0.5">Stock bas</div>
        </div>
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #ef4444', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-2"><X size={18} className="text-red-600" /><span className="text-xs font-semibold text-gray-500">Ruptures</span></div>
          <div className="text-2xl font-bold text-red-600">{totaux.nb_rupture || 0}</div>
        </div>
        <div className="flex gap-2">
          <div onClick={openCreateArticle} className="flex-1 bg-white rounded-2xl p-4 cursor-pointer group transition-all hover:shadow-md" style={{ border: '2px dashed #d1d5db' }}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = '#448696'} onMouseLeave={(e) => e.currentTarget.style.borderColor = '#d1d5db'}>
            <div className="flex flex-col items-center justify-center h-full gap-1">
              <Plus size={20} style={{ color: '#448696' }} />
              <span className="text-xs font-bold text-gray-600 group-hover:text-[#448696]">Article</span>
            </div>
          </div>
          <div onClick={() => openMvtModal()} className="flex-1 bg-white rounded-2xl p-4 cursor-pointer group transition-all hover:shadow-md" style={{ border: '2px dashed #d1d5db' }}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = '#10b981'} onMouseLeave={(e) => e.currentTarget.style.borderColor = '#d1d5db'}>
            <div className="flex flex-col items-center justify-center h-full gap-1">
              <ArrowDown size={20} className="text-green-600" />
              <span className="text-xs font-bold text-gray-600 group-hover:text-green-600">Mouvement</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-6" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input type="text" placeholder="Rechercher par référence, désignation..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" {...inputStyle} />
          </div>
          <div className="flex gap-2 flex-wrap">
            {categories.length > 0 && (
              <select value={filterCategorie} onChange={(e) => setFilterCategorie(e.target.value)}
                className="px-4 py-2.5 rounded-xl text-xs font-semibold border-2 border-gray-200 text-gray-600 focus:outline-none">
                <option value="">Toutes catégories</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
            <button onClick={() => setFilterAlerte(!filterAlerte)}
              className={`px-4 py-2.5 rounded-xl text-xs font-semibold border-2 flex items-center gap-1 transition-all ${filterAlerte ? 'border-amber-400 bg-amber-50 text-amber-700 shadow-sm' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              <AlertTriangle size={13} /> Alertes
            </button>
           <button onClick={() => setShowEntrepotModal(true)} className="px-4 py-2.5 rounded-xl text-xs font-semibold border-2 border-gray-200 text-gray-600 hover:bg-gray-50 flex items-center gap-1">
              <Warehouse size={13} /> Entrepôts
            </button>
            <button onClick={async () => {
              try {
                showToast('Génération de l\'inventaire PDF...', 'loading');
                await api.stocks.exportInventaire({ categorie: filterCategorie, alerte: filterAlerte });
                showToast('Inventaire PDF téléchargé !', 'success');
              } catch (e) { showToast('Erreur génération PDF', 'error'); }
            }} className="px-4 py-2.5 rounded-xl text-xs font-semibold border-2 border-gray-200 text-gray-600 hover:bg-gray-50 flex items-center gap-1">
              <Download size={13} /> Export PDF
            </button>
          </div>
        </div>
      </div>

      {/* Liste articles */}
      {isLoading ? null : articles.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
          <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-bold text-gray-700 mb-2">Aucun article</h3>
          <button onClick={openCreateArticle} className="mt-4 px-6 py-3 text-white rounded-xl font-semibold text-sm shadow-lg" style={{ backgroundColor: '#448696' }}><Plus className="w-4 h-4 inline mr-2" />Ajouter</button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="divide-y divide-gray-100">
            {articles.map(a => {
              const isAlerte = a.stock_minimum > 0 && a.stock_actuel <= a.stock_minimum;
              const isRupture = parseFloat(a.stock_actuel) === 0;
              const pctStock = a.stock_minimum > 0 ? Math.min((a.stock_actuel / a.stock_minimum) * 100, 200) : 100;
              return (
                <div key={a.id} onClick={() => loadDetail(a)} className="px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer group">
                  <div className="flex items-center gap-4">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${isRupture ? 'bg-red-50' : isAlerte ? 'bg-amber-50' : 'bg-gray-50'}`}>
                      <Package size={20} className={isRupture ? 'text-red-500' : isAlerte ? 'text-amber-500' : 'text-gray-400'} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-bold text-gray-900 truncate">{a.designation}</span>
                        <span className="text-xs font-mono text-gray-400">{a.reference}</span>
                        {isRupture && <span className="text-[10px] px-1.5 py-0.5 rounded font-bold bg-red-100 text-red-600">RUPTURE</span>}
                        {isAlerte && !isRupture && <span className="text-[10px] px-1.5 py-0.5 rounded font-bold bg-amber-100 text-amber-600">STOCK BAS</span>}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-gray-400 font-medium">
                        {a.categorie && <span className="flex items-center gap-1"><Tag size={10} />{a.categorie}</span>}
                        {a.entrepot_nom && <span className="flex items-center gap-1"><Warehouse size={10} />{a.entrepot_nom}</span>}
                        <span>Achat: {formatMontant(a.prix_achat)}</span>
                        <span>Vente: {formatMontant(a.prix_vente)}</span>
                      </div>
                    </div>
                    {/* Stock */}
                    <div className="text-right shrink-0 min-w-[100px]">
                      <div className={`text-lg font-bold ${isRupture ? 'text-red-600' : isAlerte ? 'text-amber-600' : ''}`} style={!isRupture && !isAlerte ? { color: '#1a1a2e' } : {}}>
                        {a.stock_actuel} <span className="text-xs font-normal text-gray-400">{a.unite}</span>
                      </div>
                      {a.stock_minimum > 0 && (
                        <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden mt-1 ml-auto">
                          <div className="h-full rounded-full" style={{ width: `${Math.min(pctStock, 100)}%`, backgroundColor: isRupture ? '#ef4444' : isAlerte ? '#f59e0b' : '#10b981' }} />
                        </div>
                      )}
                    </div>
                    {/* Actions rapides */}
                    <div className="opacity-0 group-hover:opacity-100 flex gap-1">
                      <button onClick={(e) => { e.stopPropagation(); openMvtModal(a.id, 'entree'); }} className="w-8 h-8 rounded-lg flex items-center justify-center bg-green-50 hover:bg-green-100" title="Entrée"><ArrowDown size={14} className="text-green-600" /></button>
                      <button onClick={(e) => { e.stopPropagation(); openMvtModal(a.id, 'sortie'); }} className="w-8 h-8 rounded-lg flex items-center justify-center bg-red-50 hover:bg-red-100" title="Sortie"><ArrowUp size={14} className="text-red-600" /></button>
                      <button onClick={(e) => { e.stopPropagation(); openEditArticle(a); }} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"><Edit2 size={14} className="text-gray-400" /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );

  // ============================================================================
  // VUE DÉTAIL
  // ============================================================================
  const renderDetail = () => {
    if (loadingDetail) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-gray-300" /></div>;
    if (!selectedArticle) return null;
    const a = selectedArticle;
    const isAlerte = a.stock_minimum > 0 && a.stock_actuel <= a.stock_minimum;
    const isRupture = parseFloat(a.stock_actuel) === 0;
    const valeur = parseFloat(a.stock_actuel) * parseFloat(a.prix_achat);

    return (
      <>
        <button onClick={() => { setCurrentView('list'); setSelectedArticle(null); }}
          className="flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-gray-700 mb-5"><ChevronLeft size={18} /> Retour</button>

        {/* Header */}
        <div className="bg-white rounded-2xl p-6 mb-6" style={{ borderLeft: `4px solid ${isRupture ? '#ef4444' : isAlerte ? '#f59e0b' : '#448696'}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h2 className="text-2xl font-bold" style={{ color: '#1a1a2e' }}>{a.designation}</h2>
                {isRupture && <span className="text-xs px-2 py-1 rounded-lg font-bold bg-red-100 text-red-600">RUPTURE</span>}
                {isAlerte && !isRupture && <span className="text-xs px-2 py-1 rounded-lg font-bold bg-amber-100 text-amber-600">STOCK BAS</span>}
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-500">
                <span className="font-mono font-bold">{a.reference}</span>
                {a.categorie && <span className="px-2 py-0.5 bg-gray-100 rounded text-xs">{a.categorie}</span>}
                {a.entrepot_nom && <span className="flex items-center gap-1"><Warehouse size={13} />{a.entrepot_nom}</span>}
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => openMvtModal(a.id, 'entree')} className="px-4 py-2 rounded-xl text-xs font-semibold bg-green-50 text-green-700 hover:bg-green-100 flex items-center gap-1"><ArrowDown size={14} /> Entrée</button>
              <button onClick={() => openMvtModal(a.id, 'sortie')} className="px-4 py-2 rounded-xl text-xs font-semibold bg-red-50 text-red-600 hover:bg-red-100 flex items-center gap-1"><ArrowUp size={14} /> Sortie</button>
              <button onClick={() => openEditArticle(a)} className="px-4 py-2 rounded-xl text-xs font-semibold border-2 border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-1"><Edit2 size={14} /> Modifier</button>
            </div>
          </div>
        </div>

        {/* KPIs article */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-2xl p-4" style={{ borderLeft: `3px solid ${isRupture ? '#ef4444' : isAlerte ? '#f59e0b' : '#10b981'}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div className="text-xs font-semibold text-gray-500 mb-1">Stock actuel</div>
            <div className={`text-2xl font-bold ${isRupture ? 'text-red-600' : isAlerte ? 'text-amber-600' : 'text-green-600'}`}>{a.stock_actuel} <span className="text-xs font-normal">{a.unite}</span></div>
            {a.stock_minimum > 0 && <div className="text-xs text-gray-400">Min: {a.stock_minimum}</div>}
          </div>
          <div className="bg-white rounded-2xl p-4" style={{ borderLeft: '3px solid #6366f1', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div className="text-xs font-semibold text-gray-500 mb-1">Valeur stock</div>
            <div className="text-xl font-bold text-indigo-600">{formatMontant(valeur)}</div>
          </div>
          <div className="bg-white rounded-2xl p-4" style={{ borderLeft: '3px solid #448696', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div className="text-xs font-semibold text-gray-500 mb-1">Prix achat</div>
            <div className="text-xl font-bold" style={{ color: '#448696' }}>{formatMontant(a.prix_achat)}</div>
          </div>
          <div className="bg-white rounded-2xl p-4" style={{ borderLeft: '3px solid #f59e0b', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div className="text-xs font-semibold text-gray-500 mb-1">Prix vente</div>
            <div className="text-xl font-bold text-amber-600">{formatMontant(a.prix_vente)}</div>
          </div>
        </div>

        {/* Historique mouvements */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="px-6 py-4 border-b border-gray-100"><h3 className="text-sm font-bold text-gray-800">Historique des mouvements</h3></div>
          {(a.mouvements || []).length === 0 ? (
            <div className="p-12 text-center text-gray-400"><RefreshCw className="w-10 h-10 mx-auto mb-2 opacity-50" /><p className="text-sm">Aucun mouvement</p></div>
          ) : (
            <div className="divide-y divide-gray-50">
              {(a.mouvements || []).map((m, i) => {
                const tc = TYPE_MVT[m.type_mouvement] || TYPE_MVT.entree;
                const MIcon = tc.icon;
                return (
                  <div key={i} className="px-6 py-3 hover:bg-gray-50">
                    <div className="flex items-center gap-4">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: tc.bg }}><MIcon size={16} style={{ color: tc.color }} /></div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold" style={{ color: tc.color }}>{m.type_mouvement === 'entree' ? '+' : m.type_mouvement === 'sortie' ? '-' : '~'}{m.quantite}</span>
                          <span className="text-xs text-gray-400">{MOTIFS.find(x => x.value === m.motif)?.label || m.motif}</span>
                        </div>
                        <div className="text-xs text-gray-400">{formatDate(m.date_mouvement)} {m.description && `— ${m.description}`}</div>
                      </div>
                      <div className="text-right text-xs text-gray-500">
                        <div>{m.stock_avant} → <b>{m.stock_apres}</b></div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </>
    );
  };

  // ============================================================================
  // RENDU
  // ============================================================================
  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: '#fdfcf5' }}>
      {currentView === 'list' ? renderList() : renderDetail()}

      {/* MODAL ARTICLE */}
      {showArticleModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[92vh] overflow-y-auto">
            <div className="px-8 py-5 rounded-t-2xl flex items-center gap-3" style={{ backgroundColor: '#448696' }}>
              {articleMode === 'create' ? <Plus size={22} className="text-white" /> : <Edit2 size={22} className="text-white" />}
              <h3 className="text-lg font-bold text-white">{articleMode === 'create' ? 'Nouvel article' : 'Modifier l\'article'}</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div><label className="block text-sm font-semibold text-gray-700 mb-2">Référence *</label>
                  <input type="text" value={articleForm.reference} onChange={(e) => setArticleForm(f => ({ ...f, reference: e.target.value }))} disabled={articleMode === 'edit'}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl font-mono font-bold text-sm focus:outline-none disabled:bg-gray-100" {...inputStyle} /></div>
                <div><label className="block text-sm font-semibold text-gray-700 mb-2">Désignation *</label>
                  <input type="text" value={articleForm.designation} onChange={(e) => setArticleForm(f => ({ ...f, designation: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" {...inputStyle} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div><label className="block text-sm font-semibold text-gray-700 mb-2">Catégorie</label>
                  <input type="text" value={articleForm.categorie} onChange={(e) => setArticleForm(f => ({ ...f, categorie: e.target.value }))} placeholder="Ex: Fournitures..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" {...inputStyle} /></div>
                <div><label className="block text-sm font-semibold text-gray-700 mb-2">Unité</label>
                  <input type="text" value={articleForm.unite} onChange={(e) => setArticleForm(f => ({ ...f, unite: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" {...inputStyle} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div><label className="block text-sm font-semibold text-gray-700 mb-2">Prix d'achat</label>
                  <input type="number" value={articleForm.prix_achat} onChange={(e) => setArticleForm(f => ({ ...f, prix_achat: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" /></div>
                <div><label className="block text-sm font-semibold text-gray-700 mb-2">Prix de vente</label>
                  <input type="number" value={articleForm.prix_vente} onChange={(e) => setArticleForm(f => ({ ...f, prix_vente: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" /></div>
              </div>
              <div className="grid grid-cols-3 gap-4 mb-4">
                {articleMode === 'create' && <div><label className="block text-sm font-semibold text-gray-700 mb-2">Stock initial</label>
                  <input type="number" value={articleForm.stock_actuel} onChange={(e) => setArticleForm(f => ({ ...f, stock_actuel: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" /></div>}
                <div><label className="block text-sm font-semibold text-gray-700 mb-2">Stock minimum</label>
                  <input type="number" value={articleForm.stock_minimum} onChange={(e) => setArticleForm(f => ({ ...f, stock_minimum: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" /></div>
                <div><label className="block text-sm font-semibold text-gray-700 mb-2">Entrepôt</label>
                  <select value={articleForm.entrepot_id} onChange={(e) => setArticleForm(f => ({ ...f, entrepot_id: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none">
                    <option value="">Aucun</option>
                    {entrepots.map(e => <option key={e.id} value={e.id}>{e.nom}</option>)}
                  </select></div>
              </div>
              <div className="mb-6"><label className="block text-sm font-semibold text-gray-700 mb-2">Notes</label>
                <textarea value={articleForm.notes} onChange={(e) => setArticleForm(f => ({ ...f, notes: e.target.value }))} rows="2"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" {...inputStyle} /></div>
              <div className="flex gap-3">
                <button onClick={() => setShowArticleModal(false)} className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm border-2 border-gray-200 text-gray-700 hover:bg-gray-50">Annuler</button>
                <button onClick={handleSaveArticle} disabled={isSaving} className="flex-1 px-4 py-3 text-white rounded-xl font-semibold text-sm shadow-lg disabled:opacity-50" style={{ backgroundColor: '#448696' }}>
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : (articleMode === 'create' ? 'Créer' : 'Enregistrer')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL MOUVEMENT */}
      {showMvtModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4">
            <div className="px-6 py-4 rounded-t-2xl flex items-center gap-3" style={{ backgroundColor: '#448696' }}>
              <RefreshCw size={22} className="text-white" />
              <h3 className="text-lg font-bold text-white">Mouvement de stock</h3>
            </div>
            <div className="p-6">
              <div className="flex gap-2 mb-4">
                {Object.entries(TYPE_MVT).map(([k, v]) => {
                  const sel = mvtForm.type_mouvement === k; const I = v.icon;
                  return (
                    <button key={k} onClick={() => setMvtForm(f => ({ ...f, type_mouvement: k }))}
                      className={`flex-1 p-3 rounded-xl text-center transition-all border-2 ${sel ? 'shadow-sm' : 'border-gray-200 hover:bg-gray-50'}`}
                      style={sel ? { borderColor: v.color, backgroundColor: v.bg } : {}}>
                      <I size={18} className="mx-auto mb-1" style={{ color: sel ? v.color : '#9ca3af' }} />
                      <div className="text-xs font-bold" style={sel ? { color: v.color } : { color: '#6b7280' }}>{v.label}</div>
                    </button>
                  );
                })}
              </div>
              <div className="mb-4"><label className="block text-sm font-semibold text-gray-700 mb-2">Article *</label>
                <select value={mvtForm.article_id} onChange={(e) => { const art = articles.find(a => a.id === parseInt(e.target.value)); setMvtForm(f => ({ ...f, article_id: e.target.value, prix_unitaire: art ? art.prix_achat : '' })); }}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" {...inputStyle}>
                  <option value="">Sélectionner...</option>
                  {articles.map(a => <option key={a.id} value={a.id}>{a.reference} — {a.designation} (stock: {a.stock_actuel})</option>)}
                </select></div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div><label className="block text-sm font-semibold text-gray-700 mb-2">{mvtForm.type_mouvement === 'ajustement' ? 'Nouveau stock' : 'Quantité'} *</label>
                  <input type="number" value={mvtForm.quantite} onChange={(e) => setMvtForm(f => ({ ...f, quantite: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" /></div>
                <div><label className="block text-sm font-semibold text-gray-700 mb-2">Motif</label>
                  <select value={mvtForm.motif} onChange={(e) => setMvtForm(f => ({ ...f, motif: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none">
                    {MOTIFS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select></div>
              </div>
              <div className="mb-4"><label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                <input type="text" value={mvtForm.description} onChange={(e) => setMvtForm(f => ({ ...f, description: e.target.value }))} placeholder="Détails..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" {...inputStyle} /></div>
              <div className="flex gap-3">
                <button onClick={() => setShowMvtModal(false)} className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm border-2 border-gray-200 text-gray-700 hover:bg-gray-50">Annuler</button>
                <button onClick={handleSaveMvt} disabled={isSaving} className="flex-1 px-4 py-3 text-white rounded-xl font-semibold text-sm shadow-lg disabled:opacity-50" style={{ backgroundColor: '#448696' }}>
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Enregistrer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL ENTREPÔT */}
      {showEntrepotModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4">
            <div className="px-6 py-4 rounded-t-2xl flex items-center gap-3" style={{ backgroundColor: '#448696' }}>
              <Warehouse size={22} className="text-white" />
              <h3 className="text-lg font-bold text-white">Entrepôts</h3>
            </div>
            <div className="p-6">
              {entrepots.length > 0 && (
                <div className="mb-5 space-y-2">
                  {entrepots.map(e => (
                    <div key={e.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: e.couleur }} />
                      <div className="flex-1"><div className="text-sm font-bold text-gray-800">{e.nom}</div>
                        <div className="text-xs text-gray-400">{e.nb_articles || 0} articles — {formatMontant(e.valeur_stock)}</div></div>
                    </div>
                  ))}
                </div>
              )}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-bold text-gray-700 mb-3">Ajouter un entrepôt</h4>
                <div className="mb-3"><input type="text" placeholder="Nom *" value={entrepotForm.nom} onChange={(e) => setEntrepotForm(f => ({ ...f, nom: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" {...inputStyle} /></div>
                <div className="mb-3"><input type="text" placeholder="Adresse" value={entrepotForm.adresse} onChange={(e) => setEntrepotForm(f => ({ ...f, adresse: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" {...inputStyle} /></div>
                <div className="flex gap-3">
                  <button onClick={() => setShowEntrepotModal(false)} className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm border-2 border-gray-200 text-gray-700 hover:bg-gray-50">Fermer</button>
                  <button onClick={handleSaveEntrepot} disabled={isSaving} className="flex-1 px-4 py-3 text-white rounded-xl font-semibold text-sm shadow-lg disabled:opacity-50" style={{ backgroundColor: '#448696' }}>
                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Créer'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {toast.show && <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ show: false, message: '', type: '' })} />}
    </div>
  );
};

export default StocksModule;